import { Map, List } from 'immutable'
import { createSelector } from 'reselect'
import { get, getIn, getOr, getInOr, pick, safeToJS } from 'utils/immutable'
import { PRODUCTION_SCHEDULE_RESPONSE_RECEIVED } from 'actions/dashBuilder'

export const dashBuilderSelector = getOr(Map(), 'dashBuilder')

export const getSelectedScheduleDate = createSelector(
  dashBuilderSelector,
  getIn(['dashboard', 'selectedScheduleDate'])
)

export const getLocations = createSelector(
  dashBuilderSelector,
  get('locations')
)

export const getCustomLayoutSearch = createSelector(
  dashBuilderSelector,
  get('customLayoutSearch')
)

const EMPTY_DASHBOARD = Map()
const selectDashboard = (state) =>
  getInOr(EMPTY_DASHBOARD, ['dashBuilder', 'dashboard'], state)

export const getDashboardSlice = createSelector(selectDashboard, safeToJS)

export const getProductionSchedule = createSelector(
  dashBuilderSelector,
  getIn(['dashboard', 'productionSchedule'])
)

export const getSelectedVehicleData = createSelector(
  dashBuilderSelector,
  getIn(['dashboard', 'selectedVehicleData']),
  safeToJS
)

export const getSelectedLocation = createSelector(
  dashBuilderSelector,
  getIn(['dashboard', 'selectedLocation'])
)

// Identity of the most recently issued production-schedule request. Used to
// discard responses belonging to superseded queries — see
// watchProductionScheduleResult in sagas/dash.js.
export const getProductionScheduleRequestId = createSelector(
  dashBuilderSelector,
  getIn(['dashboard', 'productionScheduleRequestId'])
)

/**
 * True only while the request whose result will actually be applied is in flight.
 *
 * Several production-schedule requests can be in flight at once — each carries
 * its own id so apiCall no longer de-dupes them, and watchProductionScheduleResult
 * applies only the newest. Loading therefore has to track the CURRENT request
 * specifically: a prefix/any match over the async set keeps the page behind a
 * spinner while an abandoned earlier request drains, hiding rows that already
 * loaded successfully and whose response was applied.
 *
 * Mirrors apiCall's signal construction: `${successActionType}${id || ''}`.
 */
export const getIsWaitingForCurrentProductionSchedule = createSelector(
  (state) => state.getIn(['application', 'asyncSet']),
  getProductionScheduleRequestId,
  (asyncSet, requestId) =>
    Boolean(requestId) &&
    Boolean(asyncSet) &&
    asyncSet.has(`${PRODUCTION_SCHEDULE_RESPONSE_RECEIVED}${requestId}`)
)

export const getSelectedDashType = createSelector(
  dashBuilderSelector,
  getIn(['dashboard', 'selectedDashType'])
)

export const getDataBookCodes = createSelector(
  dashBuilderSelector,
  getIn(['dashboard', 'dataBookCodes'])
)

export const getSubType = createSelector(
  dashBuilderSelector,
  getIn(['dashboard', 'subType'])
)

export const getDashMetaAttrs = createSelector(
  dashBuilderSelector,
  get('dashMeta')
)

export const getSearchValue = createSelector(
  dashBuilderSelector,
  get('searchValue')
)

export const getIsLeadSerial = createSelector(
  dashBuilderSelector,
  get('isLeadSerial')
)

export const getResultFound = createSelector(
  dashBuilderSelector,
  get('resultFound')
)

const requiredDashPropsSelector = createSelector(
  dashBuilderSelector,
  pick(['serialNumber', 'dashboardType', 'dataBookCodes'])
)

export const getRequiredDashProp = createSelector(
  requiredDashPropsSelector,
  safeToJS
)

export const getBuildDate = createSelector(
  dashBuilderSelector,
  get('buildDate')
)

export const getIsPreProduction = createSelector(
  dashBuilderSelector,
  get('isPreProduction')
)

export const getIsAftermarket = createSelector(
  dashBuilderSelector,
  get('isAftermarket')
)

export const getLeadSerial = createSelector(
  dashBuilderSelector,
  get('leadSerialNumber')
)

const engineeringViewProductionScheduleSelector = createSelector(
  getProductionSchedule,
  getSelectedLocation,
  (productionSchedule, location) => {
    if (productionSchedule) {
      return productionSchedule.reduce((acc, curr) => {
        const locationMap = new Map({
          location,
        })
        const scheduleDetail = curr.merge(locationMap)
        return acc.push(scheduleDetail)
      }, List())
    }
    return List()
  }
)

export const getEngineeringViewProductionSchedule = createSelector(
  engineeringViewProductionScheduleSelector,
  safeToJS
)
